import React, { useContext, useEffect } from 'react';
import { getUserCity, sessionLabel, setUserCity } from '@firsttable/functions';
import RestaurantList from '@discover-components/restaurant/list';
import CityHero from '@discover-components/city/hero';
import CityOtherSessions from '@discover-components/city/otherSessions';
import SuburbDirectory from '@discover-components/city/suburbDirectory';
import CityCuisineLinks from '@discover-components/city/cuisineLinks';
import useDateQueryState from '@discover-data/stores/hooks/date/useDateQueryState';
import useCitySlug, { useSyncCuisinePath } from '@discover-data/stores/hooks/useCitySlug';
import { cuisineOptions } from '@/lib/cityCuisine';
import { publicSiteUrl } from '@/lib/publicUrl';
import FeatureFlagBoolean from '@/components/analytics/components/FeatureFlagBoolean';
import { TrackProvider } from '@/components/analytics/context/AnalyticsContext';
import SEO from '@/components/SEO';
import SilverstripeContext from '@/components/context/Silverstripe';

const SUBURB_DIRECTORY_REGION_ID = 29; // Auckland-only SEO rollout.

const City = () => {
  const [date] = useDateQueryState();

  const appState = useContext(SilverstripeContext);

  const {
    page: city,
    global: { siteConfig },
  } = appState;
  const { pageAction, pageActionValue } = city;
  const [{ cuisine: activeCuisine, locationPath, pathname, session }] = useCitySlug();
  useSyncCuisinePath();
  // From the parsed location, so a cuisine named Breakfast is not reported as a session.
  const currentSession = session.toLowerCase();
  const cuisineLocation =
    city.subCities?.find((suburb) => suburb.slug === locationPath) || city.filters?.suburb || city;
  const cuisineHeading = activeCuisine
    ? `${activeCuisine.title} Restaurants in ${cuisineLocation.menuTitle}`
    : '';
  const cuisineLanding =
    activeCuisine &&
    city.cuisineLanding?.slug === activeCuisine.slug &&
    city.cuisineLanding.locationPath === locationPath
      ? city.cuisineLanding
      : undefined;
  const siteUrl = publicSiteUrl(siteConfig?.websiteURL);
  const cuisineBreadcrumbs = {
    edges: [
      ...(city.breadcrumbs?.edges || []),
      { node: { id: '', slug: pathname, menuTitle: activeCuisine?.title } },
    ],
  };

  useEffect(() => {
    if (!getUserCity()) {
      setUserCity(city?.slug || null);
    }
  }, [city.slug]);

  const templateSession = pageAction === 'session' ? pageActionValue : null;

  let metaTitle = city.metaTitleFormatted;
  if (city.metaTitleFormatted.includes('{session}') && currentSession) {
    // Sometimes currentSession is 'any' - replace this with 'Dinner'
    const currentSessionTitle =
      currentSession === 'any'
        ? 'Dinner'
        : currentSession.charAt(0).toUpperCase() + currentSession.slice(1);
    metaTitle = metaTitle.replace('{session}', currentSessionTitle);
  }

  // If the session is breakfast, lunch or Last Table, append it to the meta tags
  const { metaTags, menuTitle } = city;
  let headerTitle = menuTitle;
  const templateSessionName = sessionLabel(templateSession || '');
  if (templateSessionName) {
    headerTitle += ` ${templateSessionName} deals`;
    metaTitle = `${headerTitle} - 50% off with First Table`;
  } else {
    headerTitle += ' Restaurants';
  }

  const action = (pageAction && { [pageAction]: pageActionValue }) ?? {};
  const showSuburbDirectory = String(city.regionId) === String(SUBURB_DIRECTORY_REGION_ID);
  const sessionLinks = (
    <>
      <CityOtherSessions
        variant={showSuburbDirectory ? 'links' : 'cards'}
        city={city}
        sessionType={templateSession}
        restaurantsBySession={city.sessions}
        suburbs={city.subCities}
      />
      <CityCuisineLinks
        locationPath={city.cuisineLocationPath || city.slug}
        locationTitle={city.menuTitle}
        cuisines={cuisineOptions(city.cuisineRoutes || [])}
        activeSlug={activeCuisine?.slug}
        className={showSuburbDirectory ? undefined : 'mt-12'}
      />
    </>
  );
  const directoryLocation = city.filters?.suburb || city;
  const directoryParent =
    city.subCities?.find((suburb) => String(suburb.id) === String(directoryLocation.parentId)) ||
    city.breadcrumbs?.edges?.find(
      ({ node }) => String(node.id) === String(directoryLocation.parentId)
    )?.node;

  if (city) {
    return (
      <TrackProvider
        pageEventName="list_viewed"
        source_page="restaurant list"
        eventParams={{
          date_time: date,
          session_selected: currentSession,
          city_name: city.menuTitle,
          city_id: city.id,
        }}>
        <SEO
          title={activeCuisine ? `${cuisineHeading} | First Table` : metaTitle}
          id={city.id}
          description={
            activeCuisine
              ? `Explore ${activeCuisine.title} restaurants in ${cuisineLocation.menuTitle}. Browse menus, read diner reviews and check dining offers and available tables with First Table.`
              : city.metaDescription
          }
          ogImage={city.ogImage}
          metaTags={metaTags}
          slug={activeCuisine ? pathname : city.slug}
          canonical={activeCuisine && siteUrl ? new URL(pathname, siteUrl).href : undefined}
        />
        <FeatureFlagBoolean featureFlag="100:aa_test:list:web" defaultState="hidden" />

        <div className="sm:-mb-14">
          <CityHero
            breadcrumbs={activeCuisine ? cuisineBreadcrumbs : city.breadcrumbs}
            regionTitle={city.regionTitle || city.menuTitle}
            templateSession={templateSession}
            templateSessionName={templateSessionName}
            headerTitle={cuisineHeading || headerTitle}
            slug={city.slug}
            restaurantCount={city.regionRestaurantCount || city.restaurantCount}
          />
        </div>

        <RestaurantList
          previewRestaurants={activeCuisine ? cuisineLanding?.restaurants : city.restaurantPreview}
          regionId={city.regionId}
          regionRestaurantCount={city.regionRestaurantCount ?? city.restaurantCount}
          tags={city.tags}
          suburbs={city.subCities}
          currency={siteConfig?.currency}
          peopleOptions={city.dinerOptions}
          minDiners={city.minDiners}
          maxDiners={city.maxDiners}
          walkInsEnabled={city.walkInsEnabled}
          cityId={city.id}
          cityName={city.regionTitle || city.menuTitle}
          regionViewState={{
            latitude: city.latitude,
            longitude: city.longitude,
            zoom: city.zoom,
          }}
          {...action}
        />

        {showSuburbDirectory ? (
          <SuburbDirectory
            suburbs={city.subCities || []}
            locationTitle={directoryLocation.menuTitle}
            locationId={directoryLocation.id}
            parentId={directoryLocation.parentId}
            parentTitle={directoryParent?.menuTitle}
            sessionLinks={sessionLinks}
          />
        ) : (
          <div className="mx-auto max-w-[1200px] px-4">{sessionLinks}</div>
        )}
      </TrackProvider>
    );
  }

  return null;
};

export default City;
